/* eslint-disable no-restricted-syntax */
import { InjectProperty } from '@jkhy/frontend-utils/di';
import { ErrorType, SecurityMessage, StatusType } from '@treasury/domain/channel/mappings/security';
import { AccountService } from '@treasury/domain/channel/services/account';
import { TmButtonType } from '@treasury/presentation/components/tm-button.types';
import { html, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import {
    AccountValidationResult,
    IdentityDialogContext,
    IdentityDialogStrategy,
} from '../identity-verification-dialog.types';

export class OobOtp implements IdentityDialogStrategy {
    constructor(private context: IdentityDialogContext) {}

    public get password() {
        return this.passwordRef.value?.value || '';
    }

    public set password(value) {
        if (!this.passwordRef.value?.value) {
            return;
        }

        this.passwordRef.value.value = value;
    }

    private readonly passwordRef = createRef<HTMLInputElement>();

    private isValid = false;

    @InjectProperty()
    declare private accountService: AccountService;

    public async validateAccountPassword(password: string) {
        const result = await this.accountService.validateOneTimePassword(password);

        return {
            result,
            success: result.status === StatusType.Success,
        } as AccountValidationResult;
    }

    public renderContent() {
        const { verifying } = this.context;
        return html`<div class="caption">
                We need to verify your identity. For the security of the account, please enter your
                One-Time Passcode below.
            </div>
            <div class="input-wrapper">
                <input
                    ${ref(this.passwordRef)}
                    class="password"
                    importance="password"
                    .disabled=${verifying}
                    @input=${() => this.validateInput()}
                    autocomplete="one-time-code"
                    data-lpignore="true"
                    inputmode="numeric"
                    pattern="d{6}"
                    data-1p-ignore="true"
                />
            </div> `;
    }

    public renderActions() {
        const { securityMessage, verifying, verify, cancel } = this.context;
        if (!securityMessage.errorCode) {
            return nothing;
        }

        const canRetry = securityMessage.errorCode !== ErrorType.Locked;
        return html`<div name="actions">
            <tm-button
                importance="primary"
                .disabled=${verifying || !this.isValid}
                @click=${verify}
            >
                Verify
            </tm-button>
            ${canRetry && !!securityMessage.methodUsed
                ? html`
                      <tm-button
                          importance="secondary"
                          .disabled=${verifying}
                          @click=${() => this.resend(securityMessage)}
                      >
                          ${!securityMessage.methodUsed || securityMessage.methodUsed === 'Text'
                              ? 'Resend text message'
                              : 'Call again'}
                      </tm-button>
                      ${securityMessage.hasAlternate
                          ? html` <tm-button
                                importance="secondary"
                                .disabled=${verifying}
                                @click=${() => this.resendAlternate(securityMessage)}
                            >
                                ${!securityMessage.methodUsed ||
                                securityMessage.methodUsed === 'Text'
                                    ? 'Call me instead'
                                    : 'Text me instead'}
                            </tm-button>`
                          : nothing}
                  `
                : nothing}
            ${!this.context.disableClose
                ? html`<tm-button .type="${TmButtonType.Clear}" @click=${cancel}>
                      ${canRetry ? 'Cancel' : 'Close'}
                  </tm-button>`
                : nothing}
        </div>`;
    }

    private validateInput() {
        const { length } = this.password;
        this.isValid = length >= 6 && length <= 8;
        this.context.requestUpdate();
    }

    private resendAlternate(securityMessage: SecurityMessage) {
        this.context.reverify({
            ...securityMessage,
            status: StatusType.VerifyUsingAlternate,
            oneTimePassword: null,
            message: null,
        });
    }

    private resend(securityMessage: SecurityMessage) {
        this.context.reverify({
            ...securityMessage,
            oneTimePassword: null,
            status: null,
            errorCode: null,
            message: null,
        });
    }
}
